import { useAddress } from '@/store/selector';
import { TradeData } from '@/types/trade';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetFees } from './useGetFees';
import clsx from 'clsx';
import { formatDisplay } from '@/utils/format-display';
dayjs.extend(utc);
function CountDown({
    pairIndex,
    index,
    createdAt,
    buy,
    initialPosToken,
    leverage,
}: {
    pairIndex: number;
    index: number;
    createdAt: number | string;
    buy: boolean;
    initialPosToken: string;
    leverage: number;
}) {
    const { t } = useTranslation();

    const address = useAddress();
    const { data, mutate, isLoading, isValidating } = useGetFees(
        address,
        pairIndex,
        index,
        createdAt,
        buy,
        initialPosToken,
        leverage
    );

    const targetTime = useMemo(() => {
        if (!data?.updatedAt) return 0;
        return dayjs.utc(data.updatedAt).add(4, 'hour').valueOf();
    }, [data?.updatedAt]);
    const [timeLeft, setTimeLeft] = useState(() =>
        Math.max(targetTime - dayjs.utc().valueOf(), 0)
    );
    
    // Tính thời gian còn lại (4h kể từ updatedAt)

    // Cập nhật mỗi giây
    useEffect(() => {
        if (!targetTime) return;

        const interval = setInterval(() => {
            const diff = targetTime - dayjs.utc().valueOf();
            setTimeLeft(diff > 0 ? diff : 0);
        }, targetTime);

        return () => clearInterval(interval);
    }, [targetTime]);

    // Convert ms → hh:mm:ss
    const formatTime = (ms: number) => {
        const totalSeconds = Math.floor(ms / 1000);
        const hours = String(Math.floor(totalSeconds / 3600)).padStart(2, '0');
        const minutes = String(Math.floor((totalSeconds % 3600) / 60)).padStart(
            2,
            '0'
        );
        const seconds = String(totalSeconds % 60).padStart(2, '0');
        return `${hours}:${minutes}:${seconds}`;
    };

    const handleMutateEstimate = async () => {
        await mutate();
    };

    return (
        <div className="flex items-center justify-between mb-2">
            <h2 className="font-medium text-white"> {t('Fees')}</h2>
            <div className="flex items-center gap-1 text-xs text-[#4C4C4C]">
                <div className="flex items-center gap-1 text-xs text-[#4C4C4C]">
                    <span className="text-base">{formatTime(timeLeft)}</span>
                    <button
                        onClick={handleMutateEstimate}
                        disabled={timeLeft > 0}
                        className={clsx(
                            'text-sm bg-[#292929] px-3 py-1 ml-1 rounded-lg flex items-center gap-x-1',
                            {
                                'opacity-60': timeLeft > 0,
                            }
                        )}
                    >
                        {isValidating || isLoading ? (
                            <div
                                className="animate-spin inline-block size-4 border-3 border-[#333333] border-t-white text-white rounded-full dark:text-blue-500"
                                role="status"
                                aria-label="loading"
                            />
                        ) : (
                            t('Estimate')
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}

const CountDownView = memo(CountDown);

export default function FeesCard({ item }: { item: TradeData }) {
    const { t } = useTranslation();

    const address = useAddress();

    const { data } = useGetFees(
        address,
        item.indexPrice,
        item.index,
        item.createdAt,
        item.buy,
        item.initialPosToken,
        item.leverage
    );

    return (
        <div className="rounded-2xl bg-[#1C1C1C] p-4 text-white ">
            {/* Header */}

            <div className="border-b border-dashed border-[#292929] py-4 mb-4">
                <CountDownView
                    pairIndex={item.indexPrice}
                    index={item.index}
                    createdAt={item.createdAt}
                    buy={item.buy}
                    initialPosToken={item.initialPosToken}
                    leverage={item.leverage}
                />
            </div>

            {/* Fees list */}
            <div className="space-y-1.5 text-sm">
                <div className="flex justify-between items-center ">
                    <p className="text-[15px] text-[#4C4C4C]">
                        {t('Open fee')}
                    </p>

                    <p className="text-[15px] text-white">
                        {item.openFeeValue} WLD
                    </p>
                </div>
                <div className="flex justify-between items-center ">
                    <p className="text-[15px] text-[#4C4C4C]">
                        {t('Funding fee')}
                    </p>

                    <p className="text-[15px] text-white">
                        {formatDisplay(Number(data?.fundingFee) || 0, {
                            decimalToShow: 2,
                        })}{' '}
                        WLD
                    </p>
                </div>
                <div className="flex justify-between items-center ">
                    <p className="text-[15px] text-[#4C4C4C]">
                        {t('Rollover fee')}
                    </p>

                    <p className="text-[15px] text-white">
                        {formatDisplay(Number(data?.rolloverFee) || 0, {
                            decimalToShow: 2,
                        })}{' '}
                        WLD
                    </p>
                </div>
            </div>

            <div className="border-t border-dashed border-[#292929] my-4"></div>

            {/* Footer */}

            {data?.updatedAt && (
                <p className="text-[12px] text-[#4C4C4C] text-center">
                    {t('Last updated at')}:{' '}
                    {dayjs.utc(data.updatedAt).format('DD/MM/YYYY - HH:mm:ss')}{' '}
                </p>
            )}
        </div>
    );
}
